<template>
  <div class="tw-flex tw-flex-col tw-h-full">
    <header
      class="
        tw-flex
        tw-items-center
        tw-justify-between
        tw-gap-2
        tw-px-4
        tw-h-[60px]
        tw-shrink-0
        tw-border-solid
        tw-border-x-0
        tw-border-t-0
        tw-border-b
        tw-border-sf-slate-lighter-1
      "
    >
      <h3
        class="
          tw-min-w-0 tw-truncate tw-text-sf-slate-darker-1 tw-text-lg tw-my-0
        "
      >
        {{ headerTitle }}
      </h3>
      <SfButtonDs
        variant="tertiary"
        icon-only
        icon-before="xmark"
        :aria-label="I18n.t('common.close')"
        @click="close"
      />
    </header>

    <div v-if="loading" class="tw-p-4 sf-align-c">
      <div class="sf-spinner sf-inline-block"></div>
    </div>

    <p v-else-if="failed" class="tw-p-4 tw-text-sm">
      {{ I18n.t('common.error.try_again') }}
    </p>

    <div
      v-else-if="details"
      class="tw-flex tw-flex-col tw-flex-1 tw-min-h-0 tw-p-4"
    >
      <div
        class="
          tw-mb-1
          tw-shrink-0
          tw-text-xs
          tw-uppercase
          tw-text-sf-slate-light-2
          tw-font-medium
        "
      >
        {{ companyLabel }}
        <span v-if="details.period">{{ periodLabel }}</span>
      </div>
      <!-- Single i18n key so translators see the whole sentence; the count is
           bolded via <strong> in the string and the template name is sanitized
           by v-dompurify-html. -->
      <p
        v-dompurify-html="usageSentence"
        class="tw-mb-4 tw-shrink-0 tw-text-sm"
      ></p>

      <!-- Cards flex to fill the remaining height; each list scrolls inside its
           own card so the lines above and the card headers stay in view. -->
      <div class="tw-flex tw-flex-col tw-gap-3 tw-flex-1 tw-min-h-0">
        <AccountsGroupCard
          v-if="details.not_starred_accounts.length"
          :title="notStarredTitle"
          icon-class="fa fa-regular fa-circle tw-text-sf-slate-light-1"
          :items="details.not_starred_accounts"
        />
        <AccountsGroupCard
          v-if="details.starred_accounts.length"
          :title="starredTitle"
          icon-class="fa fa-solid fa-star sf-c-warning"
          :items="details.starred_accounts"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onUnmounted } from 'vue'
import { storeToRefs } from 'pinia'
import I18n from '@app/adapters/I18n'
import { useAccountsListDrawer } from '@app/insights/store/accounts-list-drawer'
import { useDrawer } from '@app/shared/pinia/drawer'
import SfButtonDs from '@app/shared/vue/components/sf-button/sf-button-ds.vue'
import AccountsGroupCard from './accounts-group-card.vue'

const accountsDrawerStore = useAccountsListDrawer()
const drawerStore = useDrawer()

const { columnName, details, loading, failed } =
  storeToRefs(accountsDrawerStore)

const headerTitle = computed(
  () =>
    columnName.value ||
    I18n.t('companies_overview.filters.template_starring.cell_details_title')
)

const companyLabel = computed(() =>
  I18n.t(
    'companies_overview.filters.template_starring.accounts_drawer_company',
    { company: details.value?.company_name ?? '' }
  )
)

const periodLabel = computed(() =>
  I18n.t(
    'companies_overview.filters.template_starring.accounts_drawer_period',
    { period: details.value?.period ?? '' }
  )
)

const usageSentence = computed(() =>
  I18n.t(
    'companies_overview.filters.template_starring.accounts_template_usage_html',
    {
      count: details.value?.total_count ?? 0,
      template: details.value?.template_name ?? '',
    }
  )
)

const starredTitle = computed(() =>
  I18n.t(
    'companies_overview.filters.template_starring.accounts_summary.starred',
    { count: details.value?.starred_accounts.length ?? 0 }
  )
)

const notStarredTitle = computed(() =>
  I18n.t(
    'companies_overview.filters.template_starring.accounts_summary.not_starred',
    { count: details.value?.not_starred_accounts.length ?? 0 }
  )
)

function close() {
  drawerStore.closeRightDrawerWithoutPersisting(false)
}

// Reset the drawer (data + row highlight) whenever it is no longer shown
// (closed via the ✕, the overlay, or replaced by another drawer). The content
// is gated by v-if, so it unmounts on close regardless; the next open
// repopulates the store via open().
onUnmounted(() => {
  accountsDrawerStore.clear()
})
</script>
